import { AdditionalPhones, withCountryCode } from "../utils/phones";
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Swal from "sweetalert2";
import { Pagination } from "@mui/material";
import EnquiryFollowUp from "./EnquiryFollowUp";
import EnquiryModal from "./enquiryModal";
import TableLoader from "../../Loader/TableLoader";
import {
  useFutureLeads,
  useDeleteEnquiry,
  useUpdateEnquiryStatus,
  useMyTeam,
  useAllocateLeads,
} from "../hooks/useManageEnquiry";
import { useFollowUpOptions } from "../hooks/useManageEnquiry";
import usePermissions from "../hooks/usePermissions";

import { Button, Input, Tooltip, Popover } from "antd";
import {
  FilterOutlined,
  ClearOutlined,
  SearchOutlined,
  CalendarOutlined,
  EyeOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  PhoneOutlined,
  TagsOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  TeamOutlined,
} from "@ant-design/icons";

const CustomDateInput = React.forwardRef(
  ({ value, onClick, placeholder }, ref) => (
    <div
      onClick={onClick}
      ref={ref}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 12px",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        cursor: "pointer",
        minHeight: "42px",
        background: "white",
        width: "100%",
        fontSize: "14px",
        color: value ? "#262626" : "#bfbfbf",
      }}
    >
      <CalendarOutlined
        style={{ color: "#f59e0b", fontSize: "14px", flexShrink: 0 }}
      />
      <span style={{ flex: 1 }}>{value || placeholder}</span>
    </div>
  ),
);

const EMPTY_FILTER = {
  search: "",
  start_date: "",
  end_date: "",
  is_positive: "",
};

const MODE_NONE = null;
const MODE_DELETE = "delete";
const MODE_ALLOCATE = "allocate";

const FutureLeads = () => {
  const { canDelete } = usePermissions("/cms/manageenquiry/future");

  const [pageApi, setPageApi] = useState(1);
  const [filterForm, setFilterForm] = useState(EMPTY_FILTER);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedFilter, setAppliedFilter] = useState(EMPTY_FILTER);
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  // shared checkbox state
  const [checkboxMode, setCheckboxMode] = useState(MODE_NONE);
  const [isDeleteAll, setIsDeleteAll] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // ── Allocate state ────────────────────────────────────────────────────────
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [allocateLoading, setAllocateLoading] = useState(false);
  const [showAllocateModal, setShowAllocateModal] = useState(false);

  const [statusId, setStatusId] = useState(0);
  const [statusCode, setStatusCode] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [modalIsOpen2, setIsOpen2] = useState(false);
  const [modalStatus, setModalStatus] = useState(false);
  const [followUpEnquiry, setFollowUpEnquiry] = useState(null);

  const searchDebounceRef = useRef(null);

  const {
    data: listData,
    isLoading,
    isFetching,
  } = useFutureLeads(pageApi, appliedFilter);
  const deleteEnquiry = useDeleteEnquiry();
  const updateStatus = useUpdateEnquiryStatus();
  const { data: followUpOptions = [] } = useFollowUpOptions();
  /* Allocation targets are the logged-in user's own team (self + reportees) —
     same rule as Unassigned Leads. A manager must not hand a lead to someone
     outside their reporting line; super-admin / admin still see everyone. */
  const { data: internalUsers = [] } = useMyTeam();
  const allocateLeads = useAllocateLeads();

  const followUpStatusMap = React.useMemo(() => {
    const map = {};
    followUpOptions.forEach((opt) => {
      map[String(opt.option_value)] = opt.label;
    });
    return map;
  }, [followUpOptions]);

  /* Infinite scroll: pages are accumulated and appended as the user scrolls, so
     there is no page bar. Merge by id — a lead that shifts between pages must not
     appear twice. */
  const [listAccum, setListAccum] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const sentinelRef = useRef(null);

  const pageRows = listData?.data ?? [];
  const totalUser = listData?.total ?? 0;
  const totalPages = listData?.totalPages ?? 1;

  useEffect(() => {
    if (!listData) return;
    setListAccum((prev) => {
      if (pageApi === 1) return pageRows;
      const byId = new Map(prev.map((i) => [i.id, i]));
      pageRows.forEach((i) => byId.set(i.id, i));
      return Array.from(byId.values());
    });
    setHasMore(pageApi < totalPages);
    setIsLoadingMore(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listData, pageApi]);

  // Load the next page when the sentinel below the table scrolls into view.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || isFetching || isLoadingMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsLoadingMore(true);
          setPageApi((p) => p + 1);
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, isFetching, isLoadingMore]);

  const listPageData = listAccum;

  const showCheckboxes = checkboxMode !== MODE_NONE;
  const inDeleteMode = checkboxMode === MODE_DELETE;
  const inAllocateMode = checkboxMode === MODE_ALLOCATE;

  const chkColor = inAllocateMode ? "#8b5cf6" : "#f59e0b";

  useEffect(() => {
    if (!isFetching) setIsFilterLoading(false);
  }, [isFetching]);

  useEffect(() => {
    if (!followUpEnquiry) return;
    window.history.pushState({ followUpOpen: true }, "");
    const onPop = () => setFollowUpEnquiry(null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [followUpEnquiry]);

  /* Opens the shared EnquiryFollowUp panel (calls / notes / emails / inbox).
     Used by both the row click and the Follow Up action button. */
  const openFollowUp = (item) =>
    setFollowUpEnquiry({
      id: item.id,
      name: item.name,
      service: item.message?.slice(0, 40) || "Enquiry",
      email: item.email,
      phone: item.phone,
      address: item.address,
      company: item.company,
      status:
        item.is_positive == 1 ? "Positive"
        : item.is_positive == 2 ? "Negative"
        : "Neutral",
      assignedTo: item.assign_to_name || "Unassigned",
    });

  const followUpStatusStyle = (label) => {
    const l = (label || "").toLowerCase();
    if (l === "follow up")
      return { bg: "#fffbeb", color: "#d97706", border: "#fde68a" };
    if (l === "hot lead")
      return { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" };
    if (l === "no response")
      return { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb" };
    if (l === "interested")
      return { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" };
    if (l === "mark for future")
      return { bg: "#fefce8", color: "#ca8a04", border: "#fef08a" };
    if (l === "not interested")
      return { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" };
    if (l === "meeting scheduled")
      return { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" };
    if (l === "converted")
      return { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" };
    if (l === "not responding")
      return { bg: "#f5f5f5", color: "#9ca3af", border: "#e5e7eb" };
    if (l === "closed")
      return { bg: "#f5f3ff", color: "#7c3aed", border: "#ddd6fe" };
    if (l === "negative")
      return { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" };
    return { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb" };
  };

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setFilterForm((p) => ({ ...p, search: v }));
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPageApi(1);
      setAppliedFilter((p) => ({ ...p, search: v }));
    }, 500);
  };
  const handleInputChange = (e) =>
    setFilterForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const applyFilter = () => {
    setPageApi(1);
    setIsFilterLoading(true);
    setAppliedFilter({
      ...filterForm,
      start_date: startDate
        ? moment(startDate).utc().add(1, "d").format("YYYY-MM-DD")
        : "",
      end_date: endDate
        ? moment(endDate).utc().add(1, "d").format("YYYY-MM-DD")
        : "",
    });
  };
  const clearFilter = () => {
    setFilterForm(EMPTY_FILTER);
    setStartDate("");
    setEndDate("");
    setAppliedFilter(EMPTY_FILTER);
    setPageApi(1);
  };

  const resetSelection = () => {
    setCheckboxMode(MODE_NONE);
    setIsDeleteAll([]);
    setSelectAll(false);
    setSelectedAdmin(null);
  };

  const handleCheckAll = () => {
    const n = !selectAll;
    setSelectAll(n);
    setIsDeleteAll(n ? listPageData.map((i) => i.id) : []);
  };
  const handleCheckboxChange = (item) =>
    setIsDeleteAll((p) =>
      p.includes(item.id) ? p.filter((id) => id !== item.id) : [...p, item.id],
    );

  const handleDeleteSingleRow = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Delete this lead?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then((r) => {
      if (r.isConfirmed)
        deleteEnquiry.mutate(id, {
          onSuccess: () => Swal.fire("Deleted!", "Lead deleted.", "success"),
        });
    });
  };
  const showAlertDelete = () => {
    if (isDeleteAll.length === 0) {
      Swal.fire({ icon: "error", title: "No leads selected" });
      return;
    }
    Swal.fire({
      title: "Delete selected?",
      text: `Delete ${isDeleteAll.length} lead(s)?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then((r) => {
      if (r.isConfirmed)
        deleteEnquiry.mutate(isDeleteAll, {
          onSuccess: () => {
            Swal.fire("Deleted!", "Deleted.", "success");
            resetSelection();
          },
        });
    });
  };

  const handleMarkDone = (id) => {
    Swal.fire({
      title: "Mark as Converted?",
      input: "textarea",
      inputLabel: "Remark",
      icon: "success",
      showCancelButton: true,
      confirmButtonText: "Yes, Mark Done",
      confirmButtonColor: "#16a34a",
    }).then((r) => {
      if (r.isConfirmed)
        updateStatus.mutate({ id, status: 7, message: r.value || "" });
    });
  };
  const handleNegative = (id) => {
    Swal.fire({
      title: "Mark as Negative?",
      input: "textarea",
      inputLabel: "Remark",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Mark Negative",
      confirmButtonColor: "#dc2626",
    }).then((r) => {
      if (r.isConfirmed)
        updateStatus.mutate({ id, status: 10, message: r.value || "" });
    });
  };

  // ── Allocate ──────────────────────────────────────────────────────────────
  const handleOpenAllocateModal = () => setShowAllocateModal(true);

  const handleAdminSelected = (adminObj) => {
    setSelectedAdmin(adminObj);
    setShowAllocateModal(false);
    setCheckboxMode(MODE_ALLOCATE);
    setIsDeleteAll([]);
    setSelectAll(false);
  };

  const handleConfirmAllocate = () => {
    if (!selectedAdmin) {
      toast.error("Please select an admin first.");
      return;
    }
    if (isDeleteAll.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No leads selected",
        text: "Please tick at least one lead to allocate.",
        confirmButtonColor: "#8b5cf6",
      });
      return;
    }

    const limit = selectedAdmin.lead_limit ?? 0;
    const alreadyHas = selectedAdmin.total_assigned_leads ?? 0;
    const wouldHave = alreadyHas + isDeleteAll.length;

    if (limit > 0 && wouldHave > limit) {
      const canTake = Math.max(0, limit - alreadyHas);
      Swal.fire({
        icon: "error",
        title: "Lead Limit Exceeded",
        html: `<p><strong>${selectedAdmin.name}</strong> has a lead limit of <strong>${limit}</strong>.</p>
               <p>Currently assigned: <strong>${alreadyHas}</strong> &nbsp;·&nbsp; You're trying to add: <strong>${isDeleteAll.length}</strong></p>
               <p>Can still accept: <strong style="color:${canTake > 0 ? "#16a34a" : "#dc2626"}">${canTake}</strong> more lead(s).</p>`,
        confirmButtonColor: "#8b5cf6",
      });
      return;
    }

    Swal.fire({
      title: "Confirm Allocation",
      html: `Assign <strong>${isDeleteAll.length}</strong> lead(s) to <strong>${selectedAdmin.name}</strong>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Allocate",
      confirmButtonColor: "#8b5cf6",
    }).then((r) => {
      if (!r.isConfirmed) return;
      setAllocateLoading(true);
      allocateLeads.mutate(
        { adminId: selectedAdmin.id, leadIds: isDeleteAll },
        {
          onSuccess: () => {
            Swal.fire({
              icon: "success",
              title: "Allocated!",
              text: `${isDeleteAll.length} lead(s) assigned to ${selectedAdmin.name}.`,
              confirmButtonColor: "#8b5cf6",
            });
            resetSelection();
          },
          onError: (err) =>
            Swal.fire(
              "Error",
              err?.message || "Failed to allocate leads. Please try again.",
              "error",
            ),
          onSettled: () => setAllocateLoading(false),
        },
      );
    });
  };

  const activeFiltersCount = [
    filterForm.search,
    filterForm.is_positive,
    startDate,
    endDate,
  ].filter(Boolean).length;
  const labelStyle = {
    fontWeight: 600,
    color: "#374151",
    fontSize: "13px",
    marginBottom: "6px",
    display: "flex",
    alignItems: "center",
    gap: "5px",
  };
  const statusBadge = (val) => {
    if (val == 1)
      return {
        bg: "#f0fdf4",
        color: "#16a34a",
        border: "#bbf7d0",
        label: "● Positive",
      };
    if (val == 2)
      return {
        bg: "#fef2f2",
        color: "#dc2626",
        border: "#fecaca",
        label: "● Negative",
      };
    if (val == 0)
      return {
        bg: "#eff6ff",
        color: "#2563eb",
        border: "#bfdbfe",
        label: "● Neutral",
      };
    return {
      bg: "#fffbeb",
      color: "#d97706",
      border: "#fde68a",
      label: "● Pending",
    };
  };

  return (
    <>
      <style>{`
        .custom-datepicker-wrapper{width:100%}.custom-datepicker-wrapper .react-datepicker-wrapper{width:100%}.custom-datepicker-wrapper .react-datepicker__input-container{width:100%}.custom-datepicker-wrapper .react-datepicker-popper{z-index:9999!important}.custom-datepicker-wrapper .react-datepicker{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;border:1px solid #fde68a;border-radius:12px;box-shadow:0 8px 24px rgba(245,158,11,.15);overflow:hidden;font-size:13px}.custom-datepicker-wrapper .react-datepicker__header{background:linear-gradient(135deg,#f59e0b,#d97706);border-bottom:none;padding:10px 8px 6px}.custom-datepicker-wrapper .react-datepicker__current-month{color:#fff;font-weight:700;font-size:14px;margin-bottom:4px}.custom-datepicker-wrapper .react-datepicker__day-name{color:rgba(255,255,255,.85);font-weight:600;font-size:11px;width:32px;line-height:32px;margin:1px;text-align:center}.custom-datepicker-wrapper .react-datepicker__day{width:32px;line-height:32px;margin:1px;border-radius:6px;font-size:13px;color:#374151;text-align:center}.custom-datepicker-wrapper .react-datepicker__day:hover{background:#fffbeb;color:#f59e0b}.custom-datepicker-wrapper .react-datepicker__day--selected{background:linear-gradient(135deg,#f59e0b,#d97706)!important;color:#fff!important;font-weight:600}.custom-datepicker-wrapper .react-datepicker__day--today{font-weight:700;color:#f59e0b}.custom-datepicker-wrapper .react-datepicker__navigation-icon::before{border-color:rgba(255,255,255,.8);border-width:2px 2px 0 0;height:7px;width:7px}.custom-datepicker-wrapper .react-datepicker__triangle{display:none}
        @keyframes badgePop{0%{transform:scale(1)}50%{transform:scale(1.2)}100%{transform:scale(1)}}.sel-badge{animation:badgePop .25s ease}
        @keyframes slideInRight{from{opacity:0;transform:translateX(40px)}to{opacity:1;transform:translateX(0)}}
        @keyframes meAllocModalIn{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        .alloc-admin-card{border:2px solid #e5e7eb;border-radius:12px;padding:14px 16px;cursor:pointer;transition:all .18s;background:white}
        .alloc-admin-card:hover{border-color:#8b5cf6;background:#faf5ff;transform:translateY(-1px);box-shadow:0 4px 14px rgba(139,92,246,.15)}
        .alloc-admin-card.selected{border-color:#8b5cf6;background:#faf5ff;box-shadow:0 0 0 3px rgba(139,92,246,.15)}
        .alloc-admin-card.over-limit{border-color:#fca5a5;background:#fef2f2;cursor:not-allowed;opacity:.7}
        .alloc-admin-card.over-limit:hover{transform:none;box-shadow:none}
      `}</style>

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            {/* HEADER */}
            <div
              style={{
                background: "linear-gradient(135deg,#f59e0b 0%,#d97706 100%)",
                borderRadius: "16px",
                padding: "28px 32px",
                marginBottom: "28px",
                boxShadow: "0 8px 32px rgba(245,158,11,.28)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <div
                  style={{
                    width: "56px",
                    height: "56px",
                    background: "rgba(255,255,255,.22)",
                    borderRadius: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "26px",
                    color: "white",
                  }}
                >
                  <ClockCircleOutlined />
                </div>
                <div>
                  <h4
                    style={{
                      color: "white",
                      margin: 0,
                      fontSize: "24px",
                      fontWeight: 700,
                    }}
                  >
                    Future Follow-ups
                  </h4>
                  <p
                    style={{
                      color: "rgba(255,255,255,.85)",
                      margin: 0,
                      fontSize: "14px",
                    }}
                  >
                    Leads marked for future follow-up
                  </p>
                </div>
              </div>
              <div
                style={{
                  background: "rgba(255,255,255,.18)",
                  padding: "10px 18px",
                  borderRadius: "10px",
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  fontSize: "13px",
                  color: "white",
                }}
              >
                <Link
                  to="/cms/manageenquiry"
                  style={{ color: "white", textDecoration: "none" }}
                >
                  Manage Leads
                </Link>
                <span style={{ color: "rgba(255,255,255,.5)" }}>›</span>
                <span style={{ fontWeight: 600 }}>Future</span>
              </div>
            </div>

            {/* FILTER */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                boxShadow: "0 2px 16px rgba(0,0,0,.07)",
                border: "1px solid #f0f0f0",
                marginBottom: "24px",
                overflow: "visible",
              }}
            >
              <div
                onClick={() => setFiltersOpen((o) => !o)}
                style={{
                  background: "linear-gradient(135deg,#fffbeb,#fef3c7)",
                  borderBottom: filtersOpen ? "2px solid #fde68a" : "none",
                  padding: "18px 24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      width: "38px",
                      height: "38px",
                      background: "linear-gradient(135deg,#f59e0b,#d97706)",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "16px",
                    }}
                  >
                    <FilterOutlined />
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "16px",
                        color: "#1f2937",
                      }}
                    >
                      Filters
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      Filter future leads
                    </div>
                  </div>
                </div>
                {activeFiltersCount > 0 && (
                  <span
                    style={{
                      background: "linear-gradient(135deg,#f59e0b,#d97706)",
                      color: "white",
                      borderRadius: "20px",
                      padding: "4px 14px",
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    {activeFiltersCount} active
                  </span>
                )}
                <span style={{ color: "#92400e", fontSize: "13px", marginLeft: "auto", paddingLeft: "12px" }}>
                  {filtersOpen ? "▲ Collapse" : "▼ Expand"}
                </span>
              </div>
              {filtersOpen && (
              <div style={{ padding: "24px" }}>
                <div className="row g-3">
                  <div className="col-md-3">
                    <div style={labelStyle}>
                      <SearchOutlined style={{ color: "#f59e0b" }} /> Search
                    </div>
                    <Input
                      size="large"
                      placeholder="Name, email or mobile..."
                      name="search"
                      value={filterForm.search}
                      onChange={handleSearchChange}
                      prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                      style={{ borderRadius: "8px" }}
                      allowClear
                    />
                  </div>
                  {/* <div className="col-md-3">
                    <div style={labelStyle}>
                      <TagsOutlined style={{ color: "#f59e0b" }} /> Lead Type
                    </div>
                    <select
                      className="form-select"
                      name="is_positive"
                      value={filterForm.is_positive}
                      onChange={handleInputChange}
                      style={{
                        borderRadius: "8px",
                        border: "1px solid #e0e0e0",
                        minHeight: "42px",
                        fontSize: "14px",
                        padding: "8px 12px",
                      }}
                    >
                      <option value="">All Types</option>
                      <option value="0">Neutral</option>
                      <option value="1">Positive</option>
                      <option value="2">Negative</option>
                    </select>
                  </div> */}
                  <div className="col-md-3">
                    <div style={labelStyle}>
                      <CalendarOutlined style={{ color: "#f59e0b" }} /> Start
                      Date
                    </div>
                    <div className="custom-datepicker-wrapper">
                      <DatePicker
                        selected={startDate}
                        onChange={(d) => setStartDate(d)}
                        selectsStart
                        startDate={startDate}
                        endDate={endDate}
                        dateFormat="dd MMM yyyy"
                        placeholderText="Start date"
                        isClearable={!!startDate}
                        customInput={
                          <CustomDateInput placeholder="Start date" />
                        }
                        popperPlacement="bottom-start"
                      />
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div style={labelStyle}>
                      <CalendarOutlined style={{ color: "#f59e0b" }} /> End Date
                    </div>
                    <div className="custom-datepicker-wrapper">
                      <DatePicker
                        selected={endDate}
                        onChange={(d) => setEndDate(d)}
                        selectsEnd
                        startDate={startDate}
                        endDate={endDate}
                        minDate={startDate}
                        dateFormat="dd MMM yyyy"
                        placeholderText="End date"
                        isClearable={!!endDate}
                        customInput={<CustomDateInput placeholder="End date" />}
                        popperPlacement="bottom-start"
                      />
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "20px",
                    paddingTop: "20px",
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <Button
                    size="large"
                    icon={<ClearOutlined />}
                    onClick={clearFilter}
                    style={{
                      borderRadius: "10px",
                      minWidth: "130px",
                      fontWeight: 600,
                      borderColor: "#f59e0b",
                      color: "#f59e0b",
                      height: "44px",
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    type="primary"
                    size="large"
                    icon={<FilterOutlined />}
                    onClick={applyFilter}
                    loading={isFilterLoading}
                    style={{
                      borderRadius: "10px",
                      minWidth: "130px",
                      fontWeight: 600,
                      height: "44px",
                      background: "linear-gradient(135deg,#f59e0b,#d97706)",
                      border: "none",
                      boxShadow: "0 4px 12px rgba(245,158,11,.4)",
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </div>
              )}
            </div>

            <EnquiryModal
              token={localStorage.getItem("cms_token")}
              modalIsOpen2={modalIsOpen2}
              setIsOpen2={setIsOpen2}
              id={statusId}
              setStatusId={setStatusId}
              statusCode={statusCode}
              setStatusCode={setStatusCode}
              setModalStatus={setModalStatus}
              modalStatus={modalStatus}
            />

            {/* TABLE */}
            <div
              style={{
                background: "white",
                borderRadius: "16px",
                boxShadow: "0 2px 16px rgba(0,0,0,.07)",
                border: "1px solid #f0f0f0",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "20px 24px",
                  borderBottom: "2px solid #f5f5f5",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      width: "38px",
                      height: "38px",
                      background: "linear-gradient(135deg,#f59e0b,#d97706)",
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "16px",
                    }}
                  >
                    <ClockCircleOutlined />
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: "17px",
                        color: "#1f2937",
                      }}
                    >
                      Future Leads
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      {totalUser} {totalUser === 1 ? "lead" : "leads"}
                    </div>
                  </div>
                  <span
                    style={{
                      background: "#fffbeb",
                      color: "#f59e0b",
                      borderRadius: "20px",
                      padding: "3px 12px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    {totalUser}
                  </span>
                  {isDeleteAll.length > 0 && (
                    <span
                      className="sel-badge"
                      style={{
                        background: inAllocateMode
                          ? "linear-gradient(135deg,#8b5cf6,#7c3aed)"
                          : "linear-gradient(135deg,#ef4444,#dc2626)",
                        color: "white",
                        borderRadius: "20px",
                        padding: "3px 12px",
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      {isDeleteAll.length} selected
                    </span>
                  )}
                  {/* Admin chip when in allocate mode */}
                  {inAllocateMode && selectedAdmin && (
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "#faf5ff",
                        border: "1.5px solid #c4b5fd",
                        borderRadius: "20px",
                        padding: "3px 12px",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#7c3aed",
                      }}
                    >
                      <TeamOutlined /> {selectedAdmin.name}
                      <span
                        style={{
                          background: "#ede9fe",
                          borderRadius: "10px",
                          padding: "1px 7px",
                          fontSize: "11px",
                        }}
                      >
                        Assigned: {selectedAdmin.total_assigned_leads ?? 0} / Limit: {selectedAdmin.lead_limit}
                      </span>
                    </span>
                  )}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {/* ─── ALLOCATE ─── */}
                  {inAllocateMode && (
                    <button
                      onClick={resetSelection}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "white",
                        color: "#6b7280",
                        borderRadius: "10px",
                        padding: "8px 18px",
                        fontWeight: 600,
                        fontSize: "14px",
                        border: "2px solid #e0e0e0",
                        cursor: "pointer",
                      }}
                    >
                      ✕ Cancel
                    </button>
                  )}
                  <button
                    onClick={
                      inAllocateMode
                        ? handleConfirmAllocate
                        : handleOpenAllocateModal
                    }
                    disabled={allocateLeads.isPending || allocateLoading}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      background:
                        "linear-gradient(135deg,#8b5cf6 0%,#7c3aed 100%)",
                      color: "white",
                      borderRadius: "10px",
                      padding: "8px 18px",
                      fontWeight: 600,
                      fontSize: "14px",
                      border: "none",
                      cursor: "pointer",
                      boxShadow: inAllocateMode
                        ? "0 4px 14px rgba(139,92,246,.45)"
                        : "0 4px 12px rgba(139,92,246,.3)",
                      outline: inAllocateMode ? "2px solid #7c3aed" : "none",
                      outlineOffset: "2px",
                      transition: "all 0.2s",
                    }}
                  >
                    {allocateLoading ? (
                      <span className="spinner-border spinner-border-sm text-white" />
                    ) : (
                      <>
                        <TeamOutlined />{" "}
                        {inAllocateMode ? "Confirm Allocate" : "Allocate"}
                      </>
                    )}
                  </button>

                  {/* ─── DELETE ─── */}
                  {canDelete && (<>
                  {inDeleteMode && (
                    <button
                      onClick={resetSelection}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "white",
                        color: "#6b7280",
                        borderRadius: "10px",
                        padding: "8px 18px",
                        fontWeight: 600,
                        fontSize: "14px",
                        border: "2px solid #e0e0e0",
                        cursor: "pointer",
                      }}
                    >
                      ✕ Cancel
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (checkboxMode !== MODE_DELETE) {
                        setCheckboxMode(MODE_DELETE);
                        setIsDeleteAll([]);
                        setSelectAll(false);
                        setSelectedAdmin(null);
                        return;
                      }
                      showAlertDelete();
                    }}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      background: "linear-gradient(135deg,#ef4444,#dc2626)",
                      color: "white",
                      borderRadius: "10px",
                      padding: "8px 18px",
                      fontWeight: 600,
                      fontSize: "14px",
                      border: "none",
                      cursor: "pointer",
                      boxShadow: "0 4px 12px rgba(239,68,68,.3)",
                      outline: inDeleteMode ? "2px solid #dc2626" : "none",
                      outlineOffset: "2px",
                      transition: "all 0.2s",
                    }}
                  >
                    <DeleteOutlined />{" "}
                    {inDeleteMode ? "Confirm Delete" : "Delete Selected"}
                  </button>
                  </>)}
                </div>
              </div>

              {/* ── Helper instruction bar ── */}
              {showCheckboxes && (
                <div
                  style={{
                    padding: "10px 24px",
                    background: inAllocateMode
                      ? "linear-gradient(135deg,#f5f3ff,#ede9fe)"
                      : "linear-gradient(135deg,#fef2f2,#fee2e2)",
                    borderBottom: `2px solid ${inAllocateMode ? "#c4b5fd" : "#fecaca"}`,
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    fontSize: "13px",
                    color: inAllocateMode ? "#7c3aed" : "#dc2626",
                  }}
                >
                  {inAllocateMode ? (
                    <>
                      <TeamOutlined /> <strong>Allocate mode:</strong>&nbsp;Tick
                      leads to assign to <strong>{selectedAdmin?.name}</strong>,
                      then click <strong>Confirm Allocate</strong>.
                    </>
                  ) : (
                    <>
                      <DeleteOutlined /> <strong>Delete mode:</strong>&nbsp;Tick
                      leads to delete, then click{" "}
                      <strong>Confirm Delete</strong>.
                    </>
                  )}
                  <span style={{ marginLeft: "auto", fontWeight: 700 }}>
                    {isDeleteAll.length} / {listPageData.length} selected on
                    this page
                  </span>
                </div>
              )}

              <div className="table-responsive">
                <table style={{ width: "100%", minWidth: 1100, borderCollapse: "collapse" }}>
                  <thead>
                    <tr
                      style={{
                        background: "linear-gradient(135deg,#fffbeb,#fef3c7)",
                        borderBottom: "2px solid #fde68a",
                      }}
                    >
                      {showCheckboxes && (
                        <th
                          style={{ padding: "14px 16px", textAlign: "center" }}
                        >
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleCheckAll}
                            style={{ accentColor: chkColor }}
                          />
                        </th>
                      )}
                      {[
                        "Name",
                        "Email",
                        "Mobile",
                        // "UTM Sources",
                        // "Sources",
                        "Assigned To",
                        "Message",
                        "Date",
                        "Follow-up Date",
                        "Status",
                        "Actions",
                      ].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: "14px 16px",
                            textAlign: "center",
                            fontSize: "12px",
                            fontWeight: 700,
                            color: "#d97706",
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <TableLoader />
                    ) : listPageData.length > 0 ? (
                      listPageData.map((item, idx) => {
                        const badge = statusBadge(item.is_positive);
                        const isSel = isDeleteAll.includes(item.id);
                        const rowBg = isSel
                          ? inAllocateMode
                            ? "#f5f3ff"
                            : "#fffbeb"
                          : idx % 2 === 0
                            ? "white"
                            : "#fffdf7";
                        return (
                          <tr
                            key={item.id}
                            // Row click opens the lead's activity panel, same as Inbox.
                            // Checkbox mode is left alone so selection still works.
                            onClick={() => { if (!showCheckboxes) openFollowUp(item); }}
                            style={{
                              borderBottom: "1px solid #f5f5f5",
                              background: rowBg,
                              cursor: showCheckboxes ? "default" : "pointer",
                            }}
                            onMouseEnter={(e) => {
                              if (!isSel)
                                e.currentTarget.style.background = "#fffbeb";
                            }}
                            onMouseLeave={(e) => {
                              if (!isSel)
                                e.currentTarget.style.background = rowBg;
                            }}
                          >
                            {showCheckboxes && (
                              <td
                                style={{
                                  padding: "14px 16px",
                                  textAlign: "center",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSel}
                                  onChange={() => handleCheckboxChange(item)}
                                  style={{
                                    accentColor: chkColor,
                                    width: "15px",
                                    height: "15px",
                                  }}
                                />
                              </td>
                            )}
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "left",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "10px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "34px",
                                    height: "34px",
                                    borderRadius: "50%",
                                    background:
                                      "linear-gradient(135deg,#f59e0b,#d97706)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "white",
                                    fontSize: "13px",
                                    fontWeight: 700,
                                    flexShrink: 0,
                                  }}
                                >
                                  {item.name
                                    ?.split(" ")
                                    .map((w) => w.charAt(0))
                                    .join("")
                                    .slice(0, 2)
                                    .toUpperCase() || "?"}
                                </div>
                                <span
                                  style={{
                                    fontWeight: 600,
                                    color: "#1f2937",
                                    fontSize: "14px",
                                  }}
                                >
                                  {item?.name}
                                </span>
                              </div>
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                fontSize: "13px",
                                color: "#4b5563",
                                minWidth: "180px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item?.email ? (
                                <span
                                  style={{
                                    background: "#eff6ff",
                                    color: "#2563eb",
                                    borderRadius: "4px",
                                    padding: "2px 8px",
                                    fontSize: "12px",
                                    display: "inline-block",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.email}
                                </span>
                              ) : (
                                <span
                                  style={{ color: "#9ca3af", fontSize: "12px" }}
                                >
                                  N/A
                                </span>
                              )}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                fontSize: "13px",
                                color: "#4b5563",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item?.phone ? (
                                <span
                                  style={{ fontWeight: 500, color: "#374151" }}
                                >
                                  {withCountryCode(item.country_code, item.phone)}
                                </span>
                              ) : (
                                <span
                                  style={{ color: "#9ca3af", fontSize: "12px" }}
                                >
                                  N/A
                                </span>
                              )}
                              {/* Alternate number, only when present */}
                              <AdditionalPhones
                                value={item?.additional_phone}
                                dot
                                max={2}
                                style={{ marginTop: "3px" }}
                                itemStyle={{ fontWeight: 500, fontSize: "12px" }}
                              />
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item?.assign_to_name ? (
                                <span
                                  style={{
                                    background: "#f0fdf4",
                                    color: "#16a34a",
                                    border: "1px solid #bbf7d0",
                                    borderRadius: "20px",
                                    padding: "3px 12px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.assign_to_name}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    background: "#f5f5f5",
                                    color: "#9ca3af",
                                    borderRadius: "20px",
                                    padding: "3px 12px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    fontStyle: "italic",
                                  }}
                                >
                                  Unassigned
                                </span>
                              )}
                            </td>
                            {/* <td
                              style={{
                                padding: "14px 16px",
                                fontSize: "13px",
                                color: "#4b5563",
                              }}
                            >
                              <div>
                                <span
                                  style={{ color: "#9ca3af", fontSize: "12px" }}
                                >
                                  Source:{" "}
                                </span>
                                {item?.utm_source || "N/A"}
                              </div>
                              <div>
                                <span
                                  style={{ color: "#9ca3af", fontSize: "12px" }}
                                >
                                  Medium:{" "}
                                </span>
                                {item?.utm_medium || "N/A"}
                              </div>
                              <div>
                                <span
                                  style={{ color: "#9ca3af", fontSize: "12px" }}
                                >
                                  Campaign:{" "}
                                </span>
                                {item?.utm_campaign
                                  ? item.utm_campaign.substring(0, 10) + "..."
                                  : "N/A"}
                              </div>
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                fontSize: "13px",
                                color: "#4b5563",
                              }}
                            >
                              <div>
                                <span
                                  style={{ color: "#9ca3af", fontSize: "12px" }}
                                >
                                  IP:{" "}
                                </span>
                                {item.system_ip ? (
                                  <a
                                    href={`https://ip-api.com/#${item.system_ip}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: "#d97706" }}
                                  >
                                    {item.system_ip}
                                  </a>
                                ) : (
                                  "N/A"
                                )}
                              </div>
                              <div>
                                <span
                                  style={{ color: "#9ca3af", fontSize: "12px" }}
                                >
                                  URL:{" "}
                                </span>
                                {item.source_url ? (
                                  <a
                                    href={item.source_url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <span
                                      style={{
                                        background: "#fffbeb",
                                        color: "#d97706",
                                        borderRadius: "4px",
                                        padding: "1px 6px",
                                        fontSize: "12px",
                                      }}
                                    >
                                      Click Here
                                    </span>
                                  </a>
                                ) : (
                                  "N/A"
                                )}
                              </div>
                            </td> */}
                            
                            <td style={{ padding: "14px 16px", fontSize: "13px", color: "#4b5563", whiteSpace: "nowrap" }}>
                              {item.message ? (
                                <Popover trigger="hover" title="Message" overlayStyle={{ maxWidth: 360 }} content={<div style={{ maxWidth: 320, maxHeight: 280, overflowY: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 13, lineHeight: 1.6 }}>{item.message}</div>}>
                                  <span style={{ cursor: "pointer", color: "#374151" }}>{item.message.length > 50 ? item.message.slice(0, 50) + "…" : item.message}</span>
                                </Popover>
                              ) : "N/A"}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                fontSize: "13px",
                                color: "#4b5563",
                                whiteSpace: "nowrap",
                                textAlign: "center",
                              }}
                            >
                              {item.created_date ||
                                moment(item.created_at).format("DD MMM YYYY")}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                fontSize: "13px",
                                textAlign: "center",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item.next_followup_date ? (
                                <span
                                  style={{
                                    background: "#fffbeb",
                                    color: "#d97706",
                                    border: "1px solid #fde68a",
                                    borderRadius: "20px",
                                    padding: "3px 12px",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                  }}
                                >
                                  <ClockCircleOutlined
                                    style={{ marginRight: 4 }}
                                  />
                                  {moment(item.next_followup_date, [
                                    "YYYY-MM-DD",
                                    "DD/MM/YYYY",
                                    "DD-MM-YYYY",
                                  ]).format("DD MMM YYYY")}
                                </span>
                              ) : (
                                <span
                                  style={{ color: "#9ca3af", fontSize: "12px" }}
                                >
                                  Not Set
                                </span>
                              )}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                minWidth: "140px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {(() => {
                                const fuLabel =
                                  item.status != null
                                    ? followUpStatusMap[String(item.status)]
                                    : null;

                                if (fuLabel) {
                                  const style = followUpStatusStyle(fuLabel);
                                  return (
                                    <span
                                      style={{
                                        background: style.bg,
                                        color: style.color,
                                        border: `1px solid ${style.border}`,
                                        borderRadius: "20px",
                                        padding: "3px 12px",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        whiteSpace: "nowrap",
                                        display: "inline-block",
                                      }}
                                    >
                                      ● {fuLabel}
                                    </span>
                                  );
                                }

                                // Fallback to is_positive badge
                                const badge = statusBadge(item.is_positive);
                                return (
                                  <span
                                    style={{
                                      background: badge.bg,
                                      color: badge.color,
                                      border: `1px solid ${badge.border}`,
                                      borderRadius: "20px",
                                      padding: "3px 12px",
                                      fontSize: "12px",
                                      fontWeight: 600,
                                      whiteSpace: "nowrap",
                                      display: "inline-block",
                                    }}
                                  >
                                    {badge.label}
                                  </span>
                                );
                              })()}
                            </td>
                            <td
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  gap: "5px",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Tooltip title="Follow Up">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); openFollowUp(item); }}
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "6px",
                                      background:
                                        "linear-gradient(135deg,#f59e0b,#d97706)",
                                      color: "white",
                                      borderRadius: "8px",
                                      border: "none",
                                      cursor: "pointer",
                                      padding: "5px 10px",
                                      boxShadow:
                                        "0 2px 8px rgba(245,158,11,.4)",
                                    }}
                                  >
                                    <EyeOutlined style={{ fontSize: "12px" }} />
                                    <ArrowRightOutlined
                                      style={{ fontSize: "11px" }}
                                    />
                                  </button>
                                </Tooltip>
                                <Tooltip title="Mark Negative">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleNegative(item.id); }}
                                    style={{
                                      width: "32px",
                                      height: "32px",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: "#fef2f2",
                                      color: "#dc2626",
                                      borderRadius: "8px",
                                      border: "1px solid #fecaca",
                                      cursor: "pointer",
                                    }}
                                  >
                                    👎
                                  </button>
                                </Tooltip>
                                {canDelete && (
                                <Tooltip title="Delete">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDeleteSingleRow(item.id); }}
                                    style={{
                                      width: "32px",
                                      height: "32px",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      background: "#fef2f2",
                                      color: "#dc2626",
                                      borderRadius: "8px",
                                      border: "1px solid #fecaca",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <DeleteOutlined />
                                  </button>
                                </Tooltip>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan={showCheckboxes ? 12 : 11}
                          style={{ padding: "60px 20px", textAlign: "center" }}
                        >
                          <div style={{ color: "#9ca3af" }}>
                            <ClockCircleOutlined
                              style={{
                                fontSize: "48px",
                                marginBottom: "12px",
                                display: "block",
                              }}
                            />
                            <div
                              style={{
                                fontSize: "16px",
                                fontWeight: 600,
                                color: "#6b7280",
                              }}
                            >
                              No Future Leads
                            </div>
                            <div
                              style={{
                                fontSize: "13px",
                                color: "#9ca3af",
                                marginTop: "4px",
                              }}
                            >
                              Leads marked for future follow-up will appear here
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Infinite scroll sentinel — crossing it pulls the next page. */}
              {listPageData.length > 0 && (
                <div ref={sentinelRef} style={{ padding: "16px 24px", textAlign: "center", background: "#fffdf7", borderTop: "1px solid #fde68a" }}>
                  {hasMore ? (
                    <span style={{ fontSize: 13, color: "#b45309", fontWeight: 600 }}>
                      {isFetching || isLoadingMore ? "Loading more leads…" : "Scroll for more"}
                    </span>
                  ) : (
                    <span style={{ fontSize: 12, color: "#9ca3af" }}>
                      All {listPageData.length} future lead{listPageData.length !== 1 ? "s" : ""} loaded
                    </span>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          ALLOCATE MODAL — Select Internal User + lead_limit check
      ══════════════════════════════════════════════════════════════════════ */}
      {showAllocateModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1200,
            background: "rgba(15,23,42,.55)",
            backdropFilter: "blur(5px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowAllocateModal(false);
          }}
        >
          <div
            style={{
              background: "white",
              borderRadius: "18px",
              width: "100%",
              maxWidth: "580px",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(139,92,246,.25)",
              animation: "meAllocModalIn .28s ease",
              border: "2px solid #ede9fe",
            }}
          >
            {/* Header */}
            <div
              style={{
                background: "linear-gradient(135deg,#8b5cf6,#7c3aed)",
                padding: "16px 22px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <div
                  style={{
                    width: "34px",
                    height: "34px",
                    background: "rgba(255,255,255,.2)",
                    borderRadius: "9px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "16px",
                  }}
                >
                  <TeamOutlined />
                </div>
                <div>
                  <div
                    style={{
                      color: "white",
                      fontWeight: 700,
                      fontSize: "15px",
                    }}
                  >
                    Allocate Leads
                  </div>
                  <div
                    style={{ color: "rgba(255,255,255,.75)", fontSize: "11px" }}
                  >
                    Select an internal user to assign leads to
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowAllocateModal(false)}
                style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,.2)",
                  border: "none",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* User list */}
            <div
              style={{
                padding: "20px 22px",
                maxHeight: "60vh",
                overflowY: "auto",
              }}
            >
              {internalUsers.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 0",
                    color: "#9ca3af",
                  }}
                >
                  <TeamOutlined
                    style={{
                      fontSize: "40px",
                      marginBottom: "10px",
                      display: "block",
                    }}
                  />
                  <div>No internal users found</div>
                </div>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {internalUsers.map((user) => {
                    const used = user.total_assigned_leads ?? 0;
                    const limit = user.lead_limit ?? 0;
                    const pct =
                      limit > 0
                        ? Math.min(100, Math.round((used / limit) * 100))
                        : 0;
                    const overLimit = limit > 0 && used >= limit;
                    return (
                      <div
                        key={user.id}
                        className={`alloc-admin-card${overLimit ? " over-limit" : ""}`}
                        onClick={() => {
                          if (!overLimit) handleAdminSelected(user);
                        }}
                        title={
                          overLimit
                            ? `${user.name} has reached their lead limit (${limit})`
                            : ""
                        }
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            <div
                              style={{
                                width: "36px",
                                height: "36px",
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(135deg,#8b5cf6,#7c3aed)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontSize: "13px",
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {user.name
                                ?.split(" ")
                                .map((w) => w.charAt(0))
                                .join("")
                                .slice(0, 2)
                                .toUpperCase() || "?"}
                            </div>
                            <div>
                              <div
                                style={{
                                  fontWeight: 700,
                                  fontSize: "14px",
                                  color: "#1f2937",
                                }}
                              >
                                {user.name}
                              </div>
                              <div
                                style={{ fontSize: "12px", color: "#6b7280" }}
                              >
                                {user.email}
                              </div>
                            </div>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <span
                              style={{
                                background: overLimit ? "#fef2f2" : "#f5f3ff",
                                color: overLimit ? "#dc2626" : "#7c3aed",
                                border: `1px solid ${overLimit ? "#fecaca" : "#ddd6fe"}`,
                                borderRadius: "20px",
                                padding: "3px 10px",
                                fontSize: "12px",
                                fontWeight: 700,
                              }}
                            >
                              {used} / {limit} leads
                            </span>
                            {overLimit && (
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#dc2626",
                                  marginTop: "3px",
                                  fontWeight: 600,
                                }}
                              >
                                Limit reached
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Progress bar */}
                        <div
                          style={{
                            background: "#f3f4f6",
                            borderRadius: "99px",
                            height: "6px",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              borderRadius: "99px",
                              width: `${pct}%`,
                              background:
                                pct >= 100
                                  ? "linear-gradient(90deg,#ef4444,#dc2626)"
                                  : pct >= 80
                                    ? "linear-gradient(90deg,#f59e0b,#d97706)"
                                    : "linear-gradient(90deg,#8b5cf6,#7c3aed)",
                              transition: "width .4s ease",
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginTop: "4px",
                            fontSize: "11px",
                            color: "#9ca3af",
                          }}
                        >
                          <span>
                            Role: <strong>{user.role}</strong>
                          </span>
                          <span>
                            {pct}% used &nbsp;·&nbsp;{" "}
                            {Math.max(0, limit - used)} remaining
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div
              style={{
                padding: "14px 22px",
                borderTop: "1px solid #f0f0f0",
                background: "#fafafa",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <button
                onClick={() => setShowAllocateModal(false)}
                style={{
                  padding: "8px 22px",
                  background: "#f1f5f9",
                  color: "#6b7280",
                  border: "1.5px solid #e2e8f0",
                  borderRadius: "10px",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {followUpEnquiry && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1050,
            background: "#f1f5f9",
            animation: "slideInRight .28s ease",
          }}
        >
          <EnquiryFollowUp
            enquiry={followUpEnquiry}
            onClose={() => setFollowUpEnquiry(null)}
            onDeleteLead={() => {
              setFollowUpEnquiry(null);
              handleDeleteSingleRow(followUpEnquiry.id);
            }}
            onNegativeClosed={() => setFollowUpEnquiry(null)}
          />
        </div>
      )}
    </>
  );
};

export default FutureLeads;
